import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { services, testimonials, contactInfo, aboutData, faqItems, differentiators } from '@/mocks/homeData';
import { fariaLimaArticles } from '@/mocks/fariaLimaBlogData';
import { useFadeIn } from '@/hooks/useFadeIn';

function DentistSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Dentist',
    name: 'Dra. Marihana Pasquali — Dentista na Faria Lima',
    image: aboutData.image,
    '@id': 'https://dramarihana.com.br/faria-lima',
    url: 'https://dramarihana.com.br/faria-lima',
    telephone: contactInfo.phone,
    email: contactInfo.email,
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Av. Brigadeiro Faria Lima, 2355 - 6º andar',
      addressLocality: 'São Paulo',
      addressRegion: 'SP',
      postalCode: '01452-000',
      addressCountry: 'BR',
    },
    geo: { '@type': 'GeoCoordinates', latitude: -23.5832, longitude: -46.6847 },
    openingHoursSpecification: [
      { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday'], opens: '09:00', closes: '19:00' },
      { '@type': 'OpeningHoursSpecification', dayOfWeek: 'Saturday', opens: '09:00', closes: '13:00' },
    ],
    description: 'Dra. Marihana Pasquali — Dentista na Faria Lima, São Paulo. Cirurgiã Dentista especialista em estética dental, Invisalign, implantes, facetas, clareamento dental e odontopediatria. Consultório premium a 5 minutos do metrô Faria Lima. Tecnologia 3D e atendimento humanizado.',
    areaServed: [
      { '@type': 'City', name: 'São Paulo' },
      { '@type': 'Place', name: 'Faria Lima' },
      { '@type': 'Place', name: 'Itaim Bibi' },
      { '@type': 'Place', name: 'Vila Olímpia' },
      { '@type': 'Place', name: 'Pinheiros' },
      { '@type': 'Place', name: 'Jardins' },
      { '@type': 'Place', name: 'Brooklin' },
      { '@type': 'Place', name: 'Vila Nova Conceição' },
    ],
    priceRange: '$$$',
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />;
}

function FAQSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((f) => ({ '@type': 'Question', name: f.question, acceptedAnswer: { '@type': 'Answer', text: f.answer } })),
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />;
}

function LandingSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Dentista Faria Lima — Dra. Marihana Pasquali',
    description: 'Dentista na Faria Lima, São Paulo. Odontologia premium: Invisalign, facetas, implantes, clareamento dental, cirurgia bucomaxilofacial e odontopediatria. A 5 minutos do metrô Faria Lima.',
    url: 'https://dramarihana.com.br/faria-lima',
    about: { '@type': 'Dentist', name: 'Dra. Marihana Pasquali' },
    mainEntity: {
      '@type': 'LocalBusiness',
      name: 'Dra. Marihana Pasquali — Odontologia na Faria Lima',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Av. Brigadeiro Faria Lima, 2355 - 6º andar',
        addressLocality: 'São Paulo', addressRegion: 'SP', postalCode: '01452-000', addressCountry: 'BR',
      },
    },
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />;
}

function HeroFariaLima() {
  return (
    <section className="relative w-full h-[480px] md:h-[620px] flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Aerial%20view%20of%20Avenida%20Brigadeiro%20Faria%20Lima%20in%20Sao%20Paulo%20Brazil%2C%20modern%20glass%20skyscrapers%20of%20financial%20district%2C%20tree-lined%20urban%20avenue%2C%20golden%20afternoon%20sunlight%20casting%20long%20shadows%2C%20vibrant%20corporate%20business%20hub%2C%20premium%20urban%20landscape%2C%20warm%20amber%20and%20cream%20tones%2C%20slight%20atmospheric%20haze%2C%20professional%20real%20estate%20photography%2C%20clean%20composition&width=1920&height=1240&seq=fl-landing-hero&orientation=landscape"
          alt="Avenida Brigadeiro Faria Lima — centro financeiro de São Paulo — dentista Faria Lima"
          className="w-full h-full object-cover object-center"
          title="Dentista na Faria Lima — Dra. Marihana Pasquali — Odontologia Premium em São Paulo"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70"></div>
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 pt-12">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-2 text-accent-300 text-xs font-semibold uppercase tracking-[0.25em] bg-accent-900/30 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
            <i className="ri-map-pin-2-fill text-accent-400"></i>
            A 5 minutos do metrô Faria Lima
          </span>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-7xl font-bold text-white leading-[1.05] mb-4">
            Dentista na{' '}
            <span className="italic font-normal text-accent-200">Faria Lima</span>
          </h1>
          <p className="text-white/80 text-sm md:text-lg leading-relaxed max-w-lg mb-8 font-light">
            Odontologia de excelência para quem vive e trabalha no coração financeiro de São Paulo. Dra. Marihana Pasquali — Cirurgiã Dentista CRO-SP 172094. Invisalign, facetas, implantes, clareamento dental e muito mais. Tecnologia 3D, atendimento premium e horários que respeitam sua agenda.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href={`https://wa.me/${contactInfo.whatsapp}?text=Olá!%20Vim%20pela%20página%20da%20Faria%20Lima%20e%20gostaria%20de%20agendar%20uma%20avaliação.`}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white text-sm md:text-base font-semibold px-10 py-4 rounded-full transition-all duration-300 cursor-pointer text-center"
            >
              <i className="ri-calendar-check-line mr-2"></i>
              Agendar Avaliação Gratuita
            </a>
            <a
              href={`tel:${contactInfo.phone}`}
              className="whitespace-nowrap border border-white/30 hover:border-white/60 text-white text-sm md:text-base font-medium px-8 py-4 rounded-full transition-all duration-300 cursor-pointer text-center backdrop-blur-sm"
            >
              <i className="ri-phone-line mr-2"></i>
              {contactInfo.phone}
            </a>
          </div>
          <div className="flex items-center gap-5 mt-8 text-white/70 text-xs">
            <span className="flex items-center gap-1.5">
              <i className="ri-star-fill text-accent-400"></i>
              <strong className="text-white">4.9</strong> Google Reviews
            </span>
            <span className="w-1 h-1 rounded-full bg-white/30"></span>
            <span className="flex items-center gap-1.5">
              <i className="ri-shield-check-line text-accent-400"></i>
              CRO-SP 172094
            </span>
            <span className="w-1 h-1 rounded-full bg-white/30"></span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line text-accent-400"></i>
              Atendimento até 19h
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustBar() {
  const { ref, isVisible } = useFadeIn(0.1);
  const trusts = [
    { label: 'CRO-SP 172094', icon: 'ri-shield-check-line' },
    { label: 'Hospital Santa Paula', icon: 'ri-hospital-line' },
    { label: 'São Leopoldo Mandic', icon: 'ri-building-2-line' },
    { label: '4.9 Google Reviews', icon: 'ri-star-fill' },
  ];

  return (
    <section ref={ref} className={`py-8 bg-background-100 border-y border-background-200 fade-section ${isVisible ? 'is-visible' : ''}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {trusts.map((t) => (
            <div key={t.label} className="flex items-center justify-center gap-2 text-sm font-medium text-foreground-700">
              <i className={`${t.icon} text-accent-500 text-lg`}></i>
              <span className="whitespace-nowrap">{t.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AboutFariaLimaSection() {
  const { ref, isVisible } = useFadeIn(0.1);
  return (
    <section ref={ref} className={`py-16 md:py-24 bg-background-50 fade-section ${isVisible ? 'is-visible' : ''}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center">
          <div className="w-full lg:w-5/12">
            <div className="rounded-2xl overflow-hidden">
              <img
                src={aboutData.image}
                alt="Dra. Marihana Pasquali — Dentista na Faria Lima — Cirurgiã Dentista CRO-SP 172094"
                className="w-full object-cover"
                title="Dentista Faria Lima — Dra. Marihana Pasquali CRO-SP 172094"
              />
            </div>
          </div>
          <div className="w-full lg:w-7/12">
            <span className="text-xs uppercase tracking-[0.2em] text-secondary-600 font-semibold mb-3 block">
              Sua Dentista na Faria Lima
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-5">
              Dra. Marihana Pasquali —{' '}
              <span className="italic font-normal text-foreground-500">Excelência em Odontologia</span>
            </h2>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-4">
              {aboutData.description}
            </p>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-6">
              {aboutData.extendedBio}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              {aboutData.stats.map((stat) => (
                <div key={stat.label} className="text-center bg-background-100 rounded-xl p-4">
                  <span className="block font-heading text-xl md:text-2xl font-bold text-accent-600">{stat.value}</span>
                  <span className="text-xs text-foreground-500 mt-1 block">{stat.label}</span>
                </div>
              ))}
            </div>
            <Link
              to="/blog-faria-lima"
              className="whitespace-nowrap inline-flex items-center gap-2 border border-foreground-200 hover:border-foreground-400 text-foreground-700 text-sm font-medium px-6 py-3 rounded-full transition-all duration-300 cursor-pointer"
            >
              <i className="ri-article-line"></i>
              Blog Exclusivo Faria Lima
              <i className="ri-arrow-right-line ml-1"></i>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function ServicesFariaLimaSection() {
  const { ref, isVisible } = useFadeIn(0.1);
  return (
    <section ref={ref} className={`py-16 md:py-24 bg-background-100 fade-section ${isVisible ? 'is-visible' : ''}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <span className="text-xs uppercase tracking-[0.2em] text-secondary-600 font-semibold mb-3 block">
            Nossos Serviços na Faria Lima
          </span>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight max-w-2xl mx-auto">
            Odontologia completa para quem busca o{' '}
            <span className="italic font-normal text-foreground-500">melhor em São Paulo</span>
          </h2>
          <p className="text-foreground-500 text-sm mt-3 max-w-xl mx-auto">
            Do clareamento dental à cirurgia bucomaxilofacial — todos os procedimentos com tecnologia 3D e atendimento personalizado.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {services.map((svc, index) => (
            <Link
              key={svc.id}
              to={`/servicos/${svc.id}`}
              className="group bg-background-50 rounded-xl p-5 card-hover cursor-pointer"
              style={{ transitionDelay: `${index * 60}ms` }}
            >
              <span className="w-11 h-11 flex items-center justify-center bg-accent-50 rounded-xl mb-4 group-hover:bg-accent-100 transition-colors">
                <i className={`${svc.icon} text-accent-600 text-xl`}></i>
              </span>
              <h3 className="font-heading text-sm font-bold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors leading-snug">
                {svc.title}
              </h3>
              <p className="text-foreground-500 text-xs leading-relaxed line-clamp-2 mb-3">
                {svc.shortDescription}
              </p>
              <span className="text-xs font-semibold text-primary-500 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                Ver serviço completo
                <i className="ri-arrow-right-line"></i>
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function DifferentiatorsFariaLima() {
  const { ref, isVisible } = useFadeIn(0.1);
  return (
    <section ref={ref} className={`py-16 md:py-24 bg-background-50 fade-section ${isVisible ? 'is-visible' : ''}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <span className="text-xs uppercase tracking-[0.2em] text-accent-600 font-semibold mb-3 block">
            Por que escolher nossa clínica
          </span>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight max-w-2xl mx-auto">
            O diferencial de ser atendido por uma{' '}
            <span className="italic font-normal text-foreground-500">especialista na Faria Lima</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {differentiators.map((d) => (
            <div key={d.title} className="bg-background-100 rounded-xl p-6 text-center card-hover">
              <span className="w-14 h-14 flex items-center justify-center bg-accent-50 rounded-2xl mx-auto mb-4">
                <i className={`${d.icon} text-accent-600 text-2xl`}></i>
              </span>
              <h3 className="font-heading text-base font-bold text-foreground-950 mb-2">{d.title}</h3>
              <p className="text-foreground-500 text-xs leading-relaxed">{d.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialsFariaLima() {
  const { ref, isVisible } = useFadeIn(0.1);
  const topTestimonials = testimonials.slice(0, 4);

  return (
    <section ref={ref} className={`py-16 md:py-24 bg-background-100 fade-section ${isVisible ? 'is-visible' : ''}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <span className="text-xs uppercase tracking-[0.2em] text-secondary-600 font-semibold mb-3 block">
            Quem já confiou no nosso trabalho
          </span>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight max-w-2xl mx-auto">
            Pacientes da <span className="italic font-normal text-foreground-500">Faria Lima</span> que transformaram seu sorriso
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {topTestimonials.map((t) => (
            <div key={t.id} className="bg-background-50 rounded-xl p-6 card-hover">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                  <img src={t.image} alt={t.name} className="w-full h-full object-cover" />
                </div>
                <div>
                  <p className="font-bold text-sm text-foreground-950">{t.name}</p>
                  <p className="text-xs text-foreground-500">{t.procedure}</p>
                </div>
                <div className="ml-auto flex items-center gap-0.5">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-accent-400 text-xs"></i>
                  ))}
                </div>
              </div>
              <p className="text-foreground-600 text-sm leading-relaxed">{t.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function MapSectionFariaLima() {
  const { ref, isVisible } = useFadeIn(0.1);
  return (
    <section ref={ref} className={`py-16 md:py-24 bg-background-50 fade-section ${isVisible ? 'is-visible' : ''}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row gap-10 items-center">
          <div className="w-full lg:w-1/2">
            <span className="text-xs uppercase tracking-[0.2em] text-secondary-600 font-semibold mb-3 block">
              Como Chegar
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-4">
              Dentista a 5 minutos do{' '}
              <strong className="font-bold text-foreground-950">metrô Faria Lima</strong>
            </h2>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-6">
              Nosso consultório está estrategicamente localizado na <strong>Avenida Brigadeiro Faria Lima, 2355 — 6º andar</strong>, a apenas 5 minutos a pé da Estação Faria Lima do metrô (Linha Amarela). Fácil acesso de carro, transporte público ou aplicativo. Valet service disponível no local.
            </p>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-6">
              Atendemos pacientes de toda a região: <strong>Itaim Bibi, Vila Olímpia, Pinheiros, Jardins, Brooklin, Vila Nova Conceição, Jardim Europa</strong> e arredores. Se você trabalha ou mora na Faria Lima, estamos a poucos passos de você.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(contactInfo.address)}`}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold px-8 py-3.5 rounded-full transition-all duration-300 cursor-pointer text-center"
              >
                <i className="ri-navigation-line mr-2"></i>
                Traçar Rota no Google Maps
              </a>
            </div>
          </div>
          <div className="w-full lg:w-1/2 rounded-xl overflow-hidden h-72 lg:h-96">
            <iframe
              src={contactInfo.googleMapsEmbed}
              title="Consultório Odontológico Dra. Marihana Pasquali - Dentista na Faria Lima - São Paulo SP"
              className="w-full h-full"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      </div>
    </section>
  );
}

function BlogTeaserFariaLima() {
  const { ref, isVisible } = useFadeIn(0.1);
  const latestArticles = fariaLimaArticles.slice(0, 3);

  return (
    <section ref={ref} className={`py-16 md:py-24 bg-background-100 fade-section ${isVisible ? 'is-visible' : ''}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-10">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] text-secondary-600 font-semibold mb-3 block">
              Blog Exclusivo
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight">
              Artigos sobre odontologia no{' '}
              <span className="italic font-normal text-foreground-500">coração de SP</span>
            </h2>
          </div>
          <Link
            to="/blog-faria-lima"
            className="whitespace-nowrap inline-flex items-center gap-2 text-foreground-700 hover:text-primary-600 text-sm font-semibold transition-colors cursor-pointer"
          >
            Ver todos os artigos
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {latestArticles.map((article) => (
            <Link
              key={article.id}
              to={`/blog-faria-lima/${article.slug}`}
              className="group bg-background-50 rounded-xl overflow-hidden card-hover cursor-pointer"
            >
              <div className="relative overflow-hidden h-44 img-zoom">
                <img src={article.image} alt={article.title} className="w-full h-full object-cover object-center" />
              </div>
              <div className="p-5">
                <span className="text-xs font-medium text-accent-600 bg-accent-50 px-2.5 py-1 rounded-full">{article.category}</span>
                <h3 className="font-heading text-sm font-bold text-foreground-950 mt-3 mb-2 leading-snug group-hover:text-primary-600 transition-colors">
                  {article.title}
                </h3>
                <span className="text-xs text-foreground-400">{article.readTime}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQFariaLima() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const { ref, isVisible } = useFadeIn(0.1);

  return (
    <section ref={ref} className={`py-16 md:py-24 bg-background-50 fade-section ${isVisible ? 'is-visible' : ''}`}>
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <span className="text-xs uppercase tracking-[0.2em] text-accent-600 font-semibold mb-3 block">
            Tire suas dúvidas
          </span>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight">
            Perguntas frequentes sobre nossa{' '}
            <span className="italic font-normal text-foreground-500">clínica na Faria Lima</span>
          </h2>
        </div>
        <div className="space-y-3">
          {faqItems.map((item, i) => (
            <div key={i} className="bg-background-100 rounded-xl overflow-hidden">
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex items-center justify-between p-5 text-left cursor-pointer"
              >
                <span className="font-heading text-sm font-bold text-foreground-950 pr-4">{item.question}</span>
                <span className={`flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full transition-all duration-300 ${openIndex === i ? 'bg-primary-500 text-white rotate-45' : 'bg-background-200 text-foreground-500'}`}>
                  <i className="ri-add-line text-sm"></i>
                </span>
              </button>
              <div className={`overflow-hidden transition-all duration-400 ${openIndex === i ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <p className="px-5 pb-5 text-foreground-600 text-sm leading-relaxed">{item.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BottomCTAFariaLima() {
  const { ref, isVisible } = useFadeIn(0.1);
  return (
    <section ref={ref} className={`py-16 md:py-24 bg-background-100 fade-section ${isVisible ? 'is-visible' : ''}`}>
      <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
        <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
          Pronto para ter o sorriso que você merece na{' '}
          <span className="italic font-normal text-foreground-500">Faria Lima</span>?
        </h2>
        <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-8 max-w-xl mx-auto">
          Agende sua avaliação personalizada com a Dra. Marihana Pasquali. Atendimento premium, tecnologia 3D e horários flexíveis para quem tem uma agenda intensa. A poucos passos do metrô Faria Lima.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href={`https://wa.me/${contactInfo.whatsapp}?text=Olá!%20Vim%20pela%20página%20da%20Faria%20Lima%20e%20gostaria%20de%20agendar%20uma%20avaliação.`}
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white text-sm md:text-base font-semibold px-10 py-4 rounded-full transition-all duration-300 cursor-pointer"
          >
            <i className="ri-whatsapp-line mr-2"></i>
            Quero Agendar Minha Avaliação
          </a>
        </div>
        <p className="text-xs text-foreground-400 mt-4">
          Ou ligue: <strong className="text-foreground-700">{contactInfo.phone}</strong>
        </p>
      </div>
    </section>
  );
}

export default function FariaLimaLandingPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      <DentistSchema />
      <FAQSchema />
      <LandingSchema />

      <main className="min-h-screen bg-background-50">
        <HeroFariaLima />
        <TrustBar />
        <AboutFariaLimaSection />
        <ServicesFariaLimaSection />
        <DifferentiatorsFariaLima />
        <TestimonialsFariaLima />
        <MapSectionFariaLima />
        <BlogTeaserFariaLima />
        <FAQFariaLima />
        <BottomCTAFariaLima />
      </main>
    </>
  );
}